import dayjs from 'dayjs'
import { ref, computed, watch } from '@vue/composition-api'
import { humanizeDuration } from '@/helpers/dateTimeHelper'
import profile from '@/http/requests/profile'
import { parseRequestError } from '@/helpers/helpers'
import organization from '@/http/requests/organization'

export default (_, { root }) => {
  const route = root.$route
  const router = root.$router
  const store = root.$store
  const organizationIdentifier = route.params.profileId
  const step = computed({
    get: () => store.state.publicStore.reservationStep,
    set: value => {
      store.commit('publicStore/SET_RESERVATION_STEP', value)
    },
  })
  const date = ref(null)
  const availableSpotsByDays = ref({ })
  const loadingSpots = ref(false)
  const tz = ref(dayjs.tz.guess())
  const routeServiceId = computed(() => {
    const id = Number(router.currentRoute.params.serviceId)
    return Number.isFinite(id) ? id : null
  })
  const routeTeacherId = computed(() => {
    const raw = router.currentRoute.params.teacherId
    if (raw === null || raw === undefined || raw === '') {
      return null
    }

    const parsed = Number(raw)
    if (Number.isFinite(parsed)) {
      return parsed
    }

    return null
  })
  const routeLocation = computed(() => {
    const raw = router.currentRoute.params.location
    if (typeof raw !== 'string' || raw.trim() === '') {
      return null
    }

    return raw
  })
  const normalizeNumericId = value => {
    if (value === null || value === undefined || value === '') {
      return null
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  const storeServiceId = computed({
    get: () => store.state.publicStore.reservationServiceId,
    set: value => {
      store.commit('publicStore/SET_RESERVATION_SERVICE_ID', normalizeNumericId(value))
    },
  })
  const selectedService = ref(null)
  const selectedLocation = computed({
    get: () => store.state.publicStore.reservationLocation,
    set: value => {
      const normalized = typeof value === 'string' && value.trim() !== '' ? value : null
      store.commit('publicStore/SET_RESERVATION_LOCATION', normalized)
    },
  })
  const selectedTutorId = computed({
    get: () => store.state.publicStore.reservationTutorId,
    set: value => {
      store.commit('publicStore/SET_RESERVATION_TUTOR_ID', normalizeNumericId(value))
    },
  })
  const selectedTutor = computed(() => {
    if (!selectedService.value?.bookableTutors?.length) {
      return null
    }

    return selectedService.value.bookableTutors.find(tutor => tutor.id === selectedTutorId.value) || null
  })
  const parametersConfirmed = computed({
    get: () => store.state.publicStore.reservationParametersConfirmed,
    set: value => {
      store.commit('publicStore/SET_RESERVATION_PARAMETERS_CONFIRMED', !!value)
    },
  })
  const availableTutorOptions = computed(() => (Array.isArray(selectedService.value?.bookableTutors)
    ? selectedService.value.bookableTutors
    : []))
  const requiresTutorSelection = computed(() => availableTutorOptions.value.length > 1)
  const isTutorReady = computed(() => {
    if (!selectedService.value) {
      return false
    }

    if (availableTutorOptions.value.length === 0) {
      return true
    }

    if (availableTutorOptions.value.length === 1) {
      return !!availableTutorOptions.value[0]?.id
    }

    return selectedTutorId.value !== null && selectedTutorId.value !== undefined && selectedTutorId.value !== ''
  })
  const availableLocations = computed(() => {
    if (!selectedService.value) {
      return []
    }

    const { serviceLocation } = selectedService.value
    if (Array.isArray(serviceLocation)) {
      return serviceLocation
    }

    return serviceLocation ? [serviceLocation] : []
  })
  const requiresLocationSelection = computed(() => availableLocations.value.length > 1)
  const isLocationReady = computed(() => {
    if (!selectedService.value) {
      return false
    }

    if (availableLocations.value.length === 0) {
      return false
    }

    if (availableLocations.value.length === 1) {
      return !!availableLocations.value[0]
    }

    return !!selectedLocation.value
  })
  const selectedSpots = computed({
    get: () => store.state.publicStore.reservationSelectedSpots,
    set: value => {
      const spots = Array.isArray(value) ? [...value] : []
      store.commit('publicStore/SET_RESERVATION_SELECTED_SPOTS', spots)
    },
  })
  const canConfirmParameters = computed(() => !!selectedService.value?.id && isTutorReady.value && isLocationReady.value)
  const canSelectDate = computed(() => parametersConfirmed.value && !!selectedService.value?.id)
  const selectedCalendarId = computed(() => {
    if (selectedTutor.value?.calendar?.id) {
      return selectedTutor.value.calendar.id
    }

    if (selectedService.value?.calendar?.id) {
      return selectedService.value.calendar.id
    }

    if (selectedService.value?.calendarId) {
      return selectedService.value.calendarId
    }

    if (Array.isArray(selectedSpots.value) && selectedSpots.value[0]?.calendarId) {
      return selectedSpots.value[0].calendarId
    }

    return null
  })
  const services = ref([])
  const bookableServices = computed(() => services.value.filter(service => service.bookable))

  const loadingServices = ref(true)
  const priceQuotation = ref(null)
  const priceQuotationLoading = ref(false)
  let priceRequestSeq = 0

  const syncReservationRoute = (overrides = {}) => {
    const hasOverride = key => Object.prototype.hasOwnProperty.call(overrides, key)

    const serviceIdCandidate = hasOverride('serviceId') ? overrides.serviceId : selectedService.value?.id
    const teacherIdCandidate = hasOverride('teacherId') ? overrides.teacherId : selectedTutorId.value
    const locationCandidate = hasOverride('location') ? overrides.location : selectedLocation.value

    const params = { profileId: organizationIdentifier }

    const serviceId = serviceIdCandidate === null || serviceIdCandidate === undefined || serviceIdCandidate === ''
      ? null
      : String(serviceIdCandidate)
    const teacherId = teacherIdCandidate === null || teacherIdCandidate === undefined || teacherIdCandidate === ''
      ? null
      : String(teacherIdCandidate)
    const location = typeof locationCandidate === 'string' && locationCandidate.trim() !== ''
      ? locationCandidate
      : null

    if (serviceId) {
      params.serviceId = serviceId
    }
    if (teacherId) {
      params.teacherId = teacherId
    }
    if (location && teacherId) {
      params.location = location
    }

    const currentParams = router.currentRoute.params || {}
    const targetServiceId = params.serviceId ?? undefined
    const targetTeacherId = params.teacherId ?? undefined
    const targetLocation = params.location ?? undefined
    const currentServiceId = currentParams.serviceId ?? undefined
    const currentTeacherId = currentParams.teacherId ?? undefined
    const currentLocation = currentParams.location ?? undefined

    if (
      targetServiceId === currentServiceId
      && targetTeacherId === currentTeacherId
      && targetLocation === currentLocation
    ) {
      return
    }

    router.replace({
      name: 'profile-reservation',
      params,
    }).catch(() => {})
  }

  const resetPriceQuotation = () => {
    priceQuotation.value = null
  }

  const notifyNoPublicReservations = () => {
    if (typeof window === 'undefined' || !window.toast || !window.toast.notify || typeof window.toast.notify.warning !== 'function') {
      return
    }

    window.toast.notify.warning(root.$t('settings.profile.reservation.toast.no-public-reservations'))
  }

  const redirectToProfile = () => {
    notifyNoPublicReservations()
    router.push({ name: 'profile', params: { profileId: organizationIdentifier } })
      .catch(() => {})
  }

  const resetSelectionState = ({
    preserveSpots = false,
    preserveTutor = false,
    preserveLocation = false,
    preserveParameters = false,
  } = {}) => {
    if (!preserveParameters) {
      parametersConfirmed.value = false
    }

    if (!preserveLocation) {
      selectedLocation.value = null
    }

    if (!preserveTutor) {
      selectedTutorId.value = null
    }

    if (!preserveSpots) {
      selectedSpots.value = []
    }

    availableSpotsByDays.value = { }
    date.value = null
    resetPriceQuotation()
    loadingSpots.value = false
  }

  const resolveInitialLocation = service => {
    if (!service) {
      return null
    }

    if (Array.isArray(service.serviceLocation) && service.serviceLocation.length === 1) {
      return service.serviceLocation[0]
    }

    if (!Array.isArray(service.serviceLocation) && service.serviceLocation) {
      return service.serviceLocation
    }

    return null
  }

  const resolveInitialTutorId = service => {
    if (!service?.bookableTutors?.length) {
      return null
    }

    if (service.bookableTutors.length === 1) {
      return service.bookableTutors[0].id
    }

    return null
  }

  const resolveRouteTutorId = service => {
    if (!service?.bookableTutors?.length) {
      return null
    }

    const tutorId = routeTeacherId.value
    if (tutorId === null) {
      return null
    }

    const match = service.bookableTutors.find(tutor => Number(tutor.id) === Number(tutorId))
    return match ? match.id : null
  }

  const resolveRouteLocation = service => {
    const location = routeLocation.value
    if (!location) {
      return null
    }

    if (Array.isArray(service?.serviceLocation)) {
      return service.serviceLocation.includes(location) ? location : null
    }

    if (service?.serviceLocation) {
      return service.serviceLocation === location ? location : null
    }

    return null
  }

  const ensureValidTutorForService = (service, tutorId) => {
    const normalized = normalizeNumericId(tutorId)
    if (normalized === null) {
      return null
    }

    if (!service?.bookableTutors?.length) {
      return null
    }

    const match = service.bookableTutors.find(tutor => Number(tutor.id) === Number(normalized))
    return match ? match.id : null
  }

  const ensureValidLocationForService = (service, location) => {
    if (typeof location !== 'string' || location.trim() === '') {
      return null
    }

    if (Array.isArray(service?.serviceLocation)) {
      return service.serviceLocation.includes(location) ? location : null
    }

    if (service?.serviceLocation) {
      return service.serviceLocation === location ? location : null
    }

    return null
  }

  const getAvailableSpots = (dateFrom = null, dateTo = null) => {
    if (!selectedService.value?.id || !parametersConfirmed.value) {
      availableSpotsByDays.value = { }
      loadingSpots.value = false
      return
    }

    loadingSpots.value = true
    let from = dateFrom
    let to = dateTo

    if (dateFrom === null) {
      from = dayjs().startOf('month').format()
    }

    if (dateTo === null) {
      to = dayjs().endOf('month').format()
    }

    const params = {
      from,
      to,
      tz: tz.value,
    }

    const calendarId = selectedTutor.value?.calendar?.id
      || selectedService.value?.calendar?.id
      || selectedService.value?.calendarId

    if (selectedLocation.value) {
      params.location = selectedLocation.value
    }

    if (calendarId) {
      params.calendarId = calendarId
    }

    organization.getEmptySpots(
      selectedService.value.id,
      params,
    )
      .then(res => {
        if (!Array.isArray(res.data)) {
          availableSpotsByDays.value = res.data
        } else {
          availableSpotsByDays.value = { }
        }
      })
      .catch(err => parseRequestError(err))
      .finally(() => {
        loadingSpots.value = false
      })
  }

  const formatPrice = value => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return '0.00 zł'
    }
    return `${numeric.toFixed(2)} zł`
  }

  const fetchPriceQuotation = () => {
    const service = selectedService.value
    if (!service || !service.id || !Array.isArray(selectedSpots.value) || selectedSpots.value.length === 0 || !parametersConfirmed.value) {
      resetPriceQuotation()
      return
    }

    const calendarId = selectedCalendarId.value
    const location = selectedLocation.value

    if (!calendarId || !location) {
      resetPriceQuotation()
      return
    }

    const spotsPayload = selectedSpots.value
      .map(spot => {
        if (!spot) return null
        const payload = {}
        if (spot.calendarEventId) {
          payload.calendarEventId = spot.calendarEventId
          payload.calendarId = spot.calendarId || calendarId
        } else {
          if (!spot.startAt) {
            return null
          }
          payload.from = spot.startAt
          payload.calendarId = spot.calendarId || calendarId
        }
        if (spot.customerId) {
          payload.customerId = spot.customerId
        }
        return payload
      })
      .filter(Boolean)

    if (!spotsPayload.length) {
      resetPriceQuotation()
      return
    }

    priceRequestSeq += 1
    const requestId = priceRequestSeq
    priceQuotationLoading.value = true
    organization.estimatePrice({
      serviceId: service.id,
      location,
      calendarId,
      tz: tz.value,
      spots: spotsPayload,
    })
      .then(res => {
        if (requestId !== priceRequestSeq) {
          return
        }
        priceQuotation.value = res.data
      })
      .catch(err => {
        if (requestId !== priceRequestSeq) {
          return
        }
        resetPriceQuotation()
        parseRequestError(err)
      })
      .finally(() => {
        if (requestId === priceRequestSeq) {
          priceQuotationLoading.value = false
        }
      })
  }

  const confirmParameters = () => {
    if (!canConfirmParameters.value || parametersConfirmed.value) {
      return
    }

    parametersConfirmed.value = true
    date.value = null
    selectedSpots.value = []
    step.value = 2
    getAvailableSpots()
  }

  const loadServices = () => {
    profile.getOrganizationPublicServices(organizationIdentifier)
      .then(res => {
        services.value = res.data
        services.value.forEach(service => {
          if (service.id === routeServiceId.value) {
            const preservingExistingSelection = storeServiceId.value === service.id
            const hasPersistedState = preservingExistingSelection && (
              parametersConfirmed.value
              || selectedSpots.value.length > 0
              || selectedTutorId.value !== null
              || (typeof selectedLocation.value === 'string' && selectedLocation.value.trim() !== '')
            )

            if (hasPersistedState) {
              resetSelectionState({
                preserveSpots: true,
                preserveTutor: true,
                preserveLocation: true,
                preserveParameters: true,
              })
            } else {
              resetSelectionState()
            }

            selectedService.value = service
            storeServiceId.value = service.id

            const tutorFromRoute = resolveRouteTutorId(service)
            const locationFromRoute = resolveRouteLocation(service)

            const tutorFromStore = hasPersistedState ? ensureValidTutorForService(service, selectedTutorId.value) : null
            const locationFromStore = hasPersistedState ? ensureValidLocationForService(service, selectedLocation.value) : null

            selectedTutorId.value = tutorFromStore ?? tutorFromRoute ?? resolveInitialTutorId(service)
            selectedLocation.value = locationFromStore ?? locationFromRoute ?? resolveInitialLocation(service)

            if (step.value !== 5) {
              step.value = 2
            }
            if (parametersConfirmed.value) {
              getAvailableSpots()
            }
            syncReservationRoute({
              serviceId: service.id,
              teacherId: selectedTutorId.value,
              location: selectedLocation.value,
            })
          }
        })
      })
      .catch(err => parseRequestError(err))
      .finally(() => {
        loadingServices.value = false
      })
  }

  const userRoles = store.getters['auth/getUserRoles'] || []
  if (Array.isArray(userRoles) && userRoles.includes('ROLE_TEACHER')) {
    loadingServices.value = false
    redirectToProfile()
  } else {
    profile.getOrganization(organizationIdentifier)
      .then(response => {
        const { data } = response
        if (!data || !data.publicReservationEnabled) {
          loadingServices.value = false
          redirectToProfile()
          return
        }
        loadServices()
      })
      .catch(err => {
        loadingServices.value = false
        parseRequestError(err)
      })
  }

  const availableSpots = computed(() => {
    if (!dayjs.isDayjs(date.value)) return []
    //
    // const number = Math.random() * (10 - 3) + 3
    // const array = []
    // let tmpDate = date.value.startOf('day')
    //   .add(8, 'hours')
    // for (let i = 0; i < number; i += 1) {
    //   array.push(tmpDate)
    //   tmpDate = tmpDate.add(30, 'minutes')
    // }
    // return array
    return availableSpotsByDays.value[date.value.format('YYYY-MM-DD')]
  })

  const serviceSelectHandler = service => {
    selectedService.value = service
    resetSelectionState()
    storeServiceId.value = service.id
    const initialLocation = resolveInitialLocation(service)
    const initialTutorId = resolveInitialTutorId(service)
    selectedLocation.value = initialLocation
    selectedTutorId.value = initialTutorId
    syncReservationRoute({
      serviceId: service.id,
      teacherId: initialTutorId,
      location: initialLocation,
    })
    step.value = 2
  }

  const removeSpot = index => {
    const updatedSpots = [...selectedSpots.value]
    updatedSpots.splice(index, 1)
    selectedSpots.value = updatedSpots
    if (updatedSpots.length === 0) {
      step.value = 2
    }
  }

  watch(selectedService, newService => {
    storeServiceId.value = newService?.id ?? null
    if (!newService?.id) {
      syncReservationRoute({ serviceId: null, teacherId: null, location: null })
      return
    }
    syncReservationRoute({ serviceId: newService.id })
  })

  watch(selectedTutorId, newTutorId => {
    if (!selectedService.value?.id) {
      return
    }
    syncReservationRoute({ teacherId: newTutorId })
  })

  watch(selectedLocation, newLocation => {
    if (!selectedService.value?.id) {
      return
    }
    syncReservationRoute({ location: newLocation })
  })

  watch(
    () => ({
      spots: selectedSpots.value,
      serviceId: selectedService.value?.id,
      timezone: tz.value,
      location: parametersConfirmed.value ? selectedLocation.value : null,
      calendarId: parametersConfirmed.value ? selectedCalendarId.value : null,
    }),
    () => {
      if (!selectedService.value || selectedSpots.value.length === 0 || !parametersConfirmed.value) {
        resetPriceQuotation()
        return
      }
      fetchPriceQuotation()
    },
    { deep: true, immediate: true },
  )

  watch(
    () => canConfirmParameters.value,
    newValue => {
      if (newValue && !parametersConfirmed.value) {
        confirmParameters()
      }
    },
  )

  const priceSummaryItems = computed(() => {
    if (!priceQuotation.value?.spots?.length) {
      return []
    }

    const aggregated = new Map()
    priceQuotation.value.spots.forEach(spot => {
      const key = spot.customerId ?? 'guest'
      const previous = aggregated.get(key) || { customerId: spot.customerId, total: 0, count: 0 }
      const numericPrice = Number(spot.price)
      if (Number.isFinite(numericPrice)) {
        previous.total += numericPrice
      }
      previous.count += 1
      aggregated.set(key, previous)
    })

    return Array.from(aggregated.entries()).map(([key, details]) => ({
      key: key === 'guest' ? 'guest' : `customer-${details.customerId}`,
      customerId: details.customerId,
      count: details.count,
      totalFormatted: formatPrice(details.total),
    }))
  })

  const priceSummaryTotal = computed(() => {
    if (!priceQuotation.value?.totalPrice) {
      return formatPrice(0)
    }
    return formatPrice(priceQuotation.value.totalPrice)
  })

  const dateChangeHandler = () => {
    step.value = 3
  }

  const monthChangedHandler = newMonth => {
    step.value = 2
    date.value = null
    if (parametersConfirmed.value) {
      getAvailableSpots(newMonth.startOf('month').format(), newMonth.endOf('month').format())
    } else {
      availableSpotsByDays.value = { }
    }
  }

  const goBackToServiceSelection = () => {
    resetSelectionState()
    selectedService.value = null
    storeServiceId.value = null
    step.value = 1
  }

  const handleBackEvent = () => {
    step.value = 1
    router.push({ name: 'profile', params: { profileId: organizationIdentifier } })
      .catch(() => {})
  }

  return {
    // data
    step,
    date,
    tz,
    selectedSpots,
    services,
    bookableServices,
    selectedService,
    availableSpotsByDays,
    loadingSpots,

    // loading flags
    loadingServices,
    priceQuotationLoading,

    // computed
    availableSpots,
    priceSummaryItems,
    priceSummaryTotal,
    selectedCalendarId,
    isTutorReady,
    isLocationReady,
    requiresTutorSelection,
    requiresLocationSelection,
    canSelectDate,
    parametersConfirmed,

    // methods
    serviceSelectHandler,
    removeSpot,
    dateChangeHandler,
    monthChangedHandler,
    handleBackEvent,
    goBackToServiceSelection,

    // tools
    dayjs,
    humanizeDuration,

    // selection state
    selectedLocation,
    selectedTutorId,
    selectedTutor,
  }
}
